import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Phone, ChevronDown, ChevronUp, Save, Loader2, Plus, X,
  AlertTriangle,
} from "lucide-react";
import {
  callsSettings, callsSettingsSave,
} from "@/lib/api";
import { toast } from "sonner";

/**
 * MissedCallsCard — plain-English setup for the missed-call text-back:
 *  - Register the Twilio phone number(s) that route to this account
 *  - Edit the auto-reply template ({business} is auto-substituted)
 *  - Optionally set an email signature for outgoing proposals / invoices
 *
 * We also surface a clear "Twilio not configured" state when the platform
 * env vars are missing, with a copy-paste webhook URL the customer can
 * plug into Twilio when they're ready.
 */
export default function MissedCallsCard() {
  const [s, setS] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newNum, setNewNum] = useState("");

  useEffect(() => {
    callsSettings().then(setS).catch(() => setS(null));
  }, []);

  const patch = async (payload) => {
    setBusy(true);
    try {
      setS(await callsSettingsSave(payload));
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const addNumber = async () => {
    const n = newNum.trim();
    if (!n) return;
    const list = [...(s.phone_numbers || []), n];
    await patch({ phone_numbers: list });
    setNewNum("");
  };

  const removeNumber = async (num) => {
    const list = (s.phone_numbers || []).filter((p) => p !== num);
    await patch({ phone_numbers: list });
  };

  if (!s) return null;

  const webhookUrl = `${window.location.origin}/api/calls/webhook/twilio`;

  return (
    <Card className="border border-slate-200 bg-white p-5" data-testid="missed-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
            <Phone className="h-4 w-4" /> Missed-call text-back
          </div>
          <h3 className="mt-1 font-heading text-lg font-bold text-slate-900">
            Turn hung-up calls into leads
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            When someone calls and no one picks up, we text them back
            instantly and drop the call in your inbox so it never falls
            through the cracks.
          </p>
        </div>
        <Badge variant="outline"
          className={s.twilio_configured
            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
            : "border-amber-300 bg-amber-50 text-amber-800"}
          data-testid="missed-status">
          {s.twilio_configured ? "Twilio ready" : "Add Twilio creds"}
        </Badge>
      </div>

      {!s.twilio_configured && (
        <div className="mt-3 flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p>
              Twilio API keys aren't set in the platform env yet. Once
              they are, your registered numbers below will auto-reply.
              You can still register the numbers now.
            </p>
            <p className="mt-1">Twilio webhook URL to paste later:</p>
            <code className="mt-1 block break-all rounded bg-white px-2 py-1 font-mono text-[11px] text-slate-900">
              {webhookUrl}
            </code>
          </div>
        </div>
      )}

      <button onClick={() => setExpanded(!expanded)}
        className="mt-3 flex items-center gap-1 text-sm font-semibold text-orange-600 hover:underline"
        data-testid="missed-toggle">
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {expanded ? "Hide settings" : "Show settings"}
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Enable */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Auto-reply:</span>
            <Button size="sm"
              variant={s.enabled ? "default" : "outline"}
              className={s.enabled ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}
              onClick={() => patch({ enabled: true })}
              disabled={busy}
              data-testid="missed-on">On</Button>
            <Button size="sm"
              variant={!s.enabled ? "default" : "outline"}
              className={!s.enabled ? "bg-slate-800 text-white hover:bg-slate-900" : ""}
              onClick={() => patch({ enabled: false })}
              disabled={busy}
              data-testid="missed-off">Off</Button>
          </div>

          {/* Numbers */}
          <div>
            <label className="text-xs font-semibold text-slate-500">
              Your phone number(s) (E.164 format, e.g. +15551234567)
            </label>
            <ul className="mt-2 space-y-1">
              {(s.phone_numbers || []).map((p) => (
                <li key={p} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-1.5 text-sm">
                  <span className="font-mono">{p}</span>
                  <button onClick={() => removeNumber(p)}
                    className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
              {(s.phone_numbers || []).length === 0 && (
                <li className="text-xs text-slate-500 italic">No numbers registered yet.</li>
              )}
            </ul>
            <div className="mt-2 flex gap-2">
              <input type="tel" value={newNum} placeholder="+15551234567"
                onChange={(e) => setNewNum(e.target.value)}
                className="flex-1 rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                data-testid="missed-new-num" />
              <Button size="sm" variant="outline" onClick={addNumber}
                disabled={busy || !newNum.trim()}
                data-testid="missed-add-num">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add
              </Button>
            </div>
          </div>

          {/* Template */}
          <div>
            <label className="text-xs font-semibold text-slate-500">
              SMS auto-reply — use <code>{"{business}"}</code> to insert your business name
            </label>
            <textarea rows={3} value={s.template}
              onChange={(e) => setS({ ...s, template: e.target.value })}
              className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              data-testid="missed-template" />
            <div className="mt-1 flex justify-end">
              <Button size="sm"
                className="bg-orange-600 text-white hover:bg-orange-700"
                onClick={() => patch({ template: s.template })}
                disabled={busy}
                data-testid="missed-save">
                {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
                Save template
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
